import React, { forwardRef, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { List, LoadingIndicator } from "colbi_web_ui/lib/components";
import styles from "./OrganizationDataForm.module.sass";
import ExportButton from "../../../components/ExportButton/ExportButton";
import { datetime } from "../../../utils";
import { EntityCard } from "colbi_web_ui/lib/components/rows";
import { SovosIcon, SovosIconButton } from "mosaic-react";
import { useTheme } from "mosaic-react/styles";
import { Refresh, SummarizeOutlined } from "mosaic-react-icons";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import isValidOrganizationReportFiscalYear from "../../../utils/isValidOrganizationReportFiscalYear";
import { fetchApi } from "../../../config/fetchConfig";
import { alias, buildRequestBody } from "../../../utils";

const organizationReportQueryBody =
    "organizationRequests{fiscalYear, startedAt, endedAt, status, organizationId, pathsToDownload{calendaryear,fiscalyear}}";

const fetchOrganizationReportRequests = async (organizationId, locale, user, dispatch) => {
    const queries = [
        {
            resource: "get_organization_requested_data",
            args: { organizationId: `${organizationId}` },
            body: organizationReportQueryBody,
        },
    ];
    const queriesBody = buildRequestBody(queries, locale);
    const response = await fetchApi("/graphql", queriesBody, {}, { dispatch, user, locale });
    const json = await response.json().catch(() => ({}));
    const queryAlias = alias({
        resource: queries[0].resource,
        args: queries[0].args,
        locale,
    });

    return json?.data?.[queryAlias] || null;
};

const resolveOrganizationId = (props, listProps) =>
    listProps?.organizationId ||
    props.organizationId ||
    props.data?.organizationId ||
    props.data?.data?.organizationId ||
    props.data?.listProps?.organizationId;

const OrganizationDataForm = (props, ref) => {
    const theme = useTheme();
    const iconColor = theme.palette?.text?.secondary ?? theme.palette?.action?.active;
    const { organization_requests, listProps, status } = props;
    const { i18n, locale } = useGlobalValue();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state) => state.user);

    const resolvedListProps = listProps || props.data?.listProps || props.data?.data?.listProps;
    const organizationId = resolveOrganizationId(props, resolvedListProps);

    const [tempListProps, setTempListProps] = useState(resolvedListProps);
    const [organizationRequestsState, setOrganizationRequestsState] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        if (resolvedListProps) {
            setTempListProps(resolvedListProps);
        }
    }, [resolvedListProps]);

    useEffect(() => {
        if (!organizationRequestsState && organization_requests?.organizationRequests) {
            setOrganizationRequestsState(organization_requests);
        }
    }, [organization_requests, organizationRequestsState]);

    const refreshOrganizationRequests = useCallback(async () => {
        if (!organizationId) {
            return;
        }

        setIsRefreshing(true);
        try {
            const nextOrganizationRequests = await fetchOrganizationReportRequests(
                organizationId,
                locale,
                reduxUser,
                dispatch,
            );
            if (nextOrganizationRequests?.organizationRequests) {
                setOrganizationRequestsState(nextOrganizationRequests);
            }
        } finally {
            setIsRefreshing(false);
        }
    }, [dispatch, locale, organizationId, reduxUser]);

    if ((status === "loading" || status === "initial") && !organizationRequestsState) {
        return <LoadingIndicator className={styles["loading-indicator"]} />;
    }

    const organizationRequestsContent = organizationRequestsState?.organizationRequests ?? [];

    const items = organizationRequestsContent
        .filter(({ fiscalYear }) => isValidOrganizationReportFiscalYear(fiscalYear))
        .map((elem) => {
            const { fiscalYear, organizationId: rowOrganizationId, startedAt, status: requestStatus, pathsToDownload } =
                elem;
            return {
                card: [
                    [
                        {
                            labels: ["fiscalYear"],
                            value: fiscalYear,
                            type: "string",
                            style: "bold",
                            align: "center",
                            basis: "10%",
                        },
                    ],
                    [
                        {
                            labels: ["requestData"],
                            value: (
                                <div
                                    className={styles["actions-user-buttons"]}
                                    onClick={() => {
                                        tempListProps?.handleRequestDataForOrganizations &&
                                            tempListProps.handleRequestDataForOrganizations(
                                                {
                                                    ...tempListProps,
                                                    onRefresh: refreshOrganizationRequests,
                                                },
                                                fiscalYear,
                                            );
                                    }}
                                >
                                    <SovosIcon>
                                        <SummarizeOutlined style={{ fill: iconColor }} />
                                    </SovosIcon>
                                </div>
                            ),
                            type: "number",
                            align: "center",
                        },
                    ],
                    [
                        {
                            labels: ["startedAt"],
                            value: startedAt ? datetime(startedAt) : "-",
                            type: "date",
                            align: "center",
                        },
                    ],
                    [
                        {
                            labels: ["status"],
                            value: i18n && i18n`${requestStatus || "-"}`,
                            type: "string",
                            align: "center",
                        },
                    ],
                    [
                        {
                            labels: ["download"],
                            value:
                                requestStatus === "done" ? (
                                    <ExportButton
                                        data={[elem]}
                                        exportableArray={Object.keys(pathsToDownload || {})}
                                        alternativeQueryData={{
                                            args: {
                                                locale: "en",
                                                organizationId: `${rowOrganizationId}`,
                                                fiscalYear: `${fiscalYear}`,
                                                selectedExportableOption: undefined,
                                                page: 0,
                                                pathsToDownload,
                                            },
                                            downloadable: true,
                                            downloadOnly: true,
                                            xlsxTranslation: "entity",
                                        }}
                                    />
                                ) : (
                                    ""
                                ),
                            type: "string",
                            align: "center",
                        },
                    ],
                ],
            };
        });

    const refreshToolbar = (
        <div className={styles["refresh-toolbar"]}>
            <SovosIconButton
                tooltipText={i18n`Reload`}
                position="end"
                onClick={refreshOrganizationRequests}
                disabled={isRefreshing || !organizationId}
                children={<Refresh />}
            />
        </div>
    );

    const listContent =
        items.length === 0 ? (
            <div className={styles["empty-state"]}>
                <small>{i18n`No organization reports available`}</small>
            </div>
        ) : (
            <List
                modifiers={["--row-style-alternate"]}
                header={[
                    {
                        label: [`fiscalYear`],
                        columnName: "fiscalYear",
                        align: "center",
                        basis: "15%",
                    },
                    {
                        label: [`Calculate Report`],
                        columnName: "requestData",
                        align: "center",
                    },
                    { label: [`Requested At`], columnName: "startedAt", align: "center" },
                    { label: [`status`], columnName: "status", align: "center" },
                    { label: [`download`], columnName: "download", align: "right" },
                ]}
                items={items}
                card={EntityCard}
            />
        );

    return (
        <>
            {refreshToolbar}
            {isRefreshing ? (
                <LoadingIndicator className={styles["loading-indicator"]} />
            ) : (
                listContent
            )}
        </>
    );
};

const OrganizationDataFormRef = forwardRef(OrganizationDataForm);

OrganizationDataFormRef.queries = (args) => {
    const organizationId =
        args.organizationId || args.data?.organizationId || args.data?.data?.organizationId;

    return [
        {
            resource: "get_organization_requested_data",
            args: {
                organizationId: organizationId ? `${organizationId}` : organizationId,
            },
            body: organizationReportQueryBody,
        },
    ].filter((q) => q.args.organizationId);
};

OrganizationDataFormRef.dataMapper = ["organization_requests"];

export default OrganizationDataFormRef;
